// Incoming, starting and active call cards with the call strip and server-gated controls.
import type { Call } from '@bcio-voice/contracts';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { Captions, Grid3x3, Mic, MicOff, Pause, Phone, PhoneForwarded, PhoneIncoming, PhoneOff, PhoneOutgoing, Play, Radio, User, Users, Volume2, WifiOff } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import type { ActionName } from '../../call/commands.ts';
import type { BridgeCall } from '../../call/bridge.ts';
import { type ActivationGuard, controlGate } from '../../call/gating.ts';
import { DIAL_KEYS, validDtmf } from '../../call/destination.ts';
import { capability } from '../../lib/capabilities.ts';
import { formatClock, formatDuration, formatPhone } from '../../lib/format.ts';
import { NETWORK_ISSUE_SOURCE, type NetworkIssue, NetworkIssueTracker, networkIssueText } from '../../call/networkIssue.ts';
import { useWorkspace } from '../../state/workspace.tsx';
import { CaptureBand } from './CaptureBand.tsx';
import { CoachingBanner } from '../coaching/CoachingBanner.tsx';
import { agentCoachingNotice } from '../coaching/coaching.ts';
import { TICKING_STATES, callLineWords, callWho, elapsedMs, humanStatusWords, type NodeState, partyName, recorderNode, stateLabel, transcriptionNode } from './callView.ts';
import { CallNoteField } from '../inbox/CallNotes.tsx';
import { draftKey, sessionDrafts } from '../inbox/notes.ts';
import { NumberTag } from '../../ui/NumberTag.tsx';
import { Link } from '../../lib/router.tsx';
import { WRAP_UP_TICK_MS, type WrapUp, type WrapUpEvent, type WrapUpPause, callHasEnded, wrapUpCountdown, wrapUpSummary } from './wrapUp.ts';
import { DropVoicemail } from './DropControl.tsx';
import { TransferControl } from './TransferControl.tsx';
import { ContextCard } from './ContextCard.tsx';
import { PostCallFollowupSection } from './PostCallFollowup.tsx';
import { MicrophoneWarning } from './MicrophoneHelp.tsx';
import { CallWindowDockRow, armCallWindowAnswer } from './CallWindowDock.tsx';

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

/** The lamp for a call's state (UX spec 3.6): ringing cues, connected is lit, held is split. */
function callLamp(state: Call['state']): 'ring' | 'live' | 'hold' | 'end' | 'neutral' {
  if (state === 'ringing') return 'ring';
  if (state === 'active') return 'live';
  if (state === 'held') return 'hold';
  if (state === 'failed') return 'end';
  return 'neutral';
}

function Node({ icon, name, state }: { icon: typeof Phone; name: string; state: NodeState }) {
  return (
    <li className={`strip-node tone-${state.tone}`}>
      <span className="strip-icon"><Icon icon={icon} /></span>
      <span className="strip-name">{name}</span>
      <span className="strip-state">
        {state.tone === 'end' && state.text === 'Recording' ? <span className="rec-dot" aria-hidden="true" /> : null}
        {state.text}
      </span>
    </li>
  );
}

/** This device's audio for a call, from the engine only. */
export function youNode(engineCall: BridgeCall | null): NodeState {
  if (!engineCall) return { text: 'No audio on this device', tone: 'warn' };
  if (engineCall.microphone === 'lost') return { text: 'Microphone lost', tone: 'end' };
  if (engineCall.media === 'recovering') return { text: 'Reconnecting audio…', tone: 'warn' };
  if (engineCall.media === 'lost') return { text: 'Audio lost', tone: 'end' };
  if (engineCall.media !== 'connected') return { text: 'Connecting audio…', tone: 'neutral' };
  if (engineCall.muted) return { text: 'Muted', tone: 'warn' };
  return { text: 'Audio connected', tone: 'live' };
}

/** The signature call strip: every party and machine attached to the call, with honest states. */
export function CallStrip({ call, party, engineCall }: { call: Call; party: string; engineCall: BridgeCall | null }) {
  const ws = useWorkspace();
  const connected = call.state === 'active';
  const held = call.state === 'held';
  const partyState: NodeState = call.state === 'ringing' ? { text: 'Ringing', tone: 'neutral' } : held ? { text: 'On hold', tone: 'hold' } : connected ? { text: 'Connected', tone: 'live' } : call.state === 'created' ? { text: 'Waiting', tone: 'neutral' } : { text: 'Left', tone: 'neutral' };
  const external = call.destination.kind === 'pstn' || call.direction === 'inbound';
  return (
    <div className={`call-strip${connected ? ' is-connected' : ''}${held ? ' is-held' : ''}`}>
      <ul className="strip-parties" aria-label="On this call">
        <Node icon={external ? Phone : Users} name={party} state={partyState} />
        <Node icon={User} name="You" state={youNode(engineCall)} />
        <Node icon={Radio} name="Recorder" state={recorderNode(call, capability(ws.config.value, 'recording.capture'))} />
        <Node icon={Captions} name="Transcription" state={transcriptionNode(call, capability(ws.config.value, 'transcription.live'))} />
      </ul>
    </div>
  );
}

/** The notice slot's command line. A command in flight ("Holding…", "Sending keys…") is shown on the pressed control
 * (aria-busy, and disabled at the same width while it waits), never here: a line that appears for one round trip on
 * every press is the flashing Allen asked to remove. Unconfirmed and failed commands stay until resolved or dismissed. */
function StatusLine({ call }: { call: Call }) {
  const ws = useWorkspace();
  const status = ws.callControl.status(call);
  if (!status || status.tone === 'pending') return null;
  return (
    <p className={`cmd-status cmd-${status.tone}`} role={status.tone === 'failed' ? 'alert' : 'status'}>
      {status.text}
      {status.requestId ? <span className="ref"> Reference <span className="mono">{status.requestId}</span></span> : null}
      {status.canCheckAgain ? <button type="button" className="btn btn-small" onClick={() => ws.callControl.checkAgain(call)}>Check again</button> : null}
      {status.canDismiss ? <button type="button" className="btn btn-small btn-quiet" onClick={() => ws.callControl.dismiss(call)}>Dismiss</button> : null}
    </p>
  );
}

/** The last network problem this device saw on the call: a client observation, labelled as one. */
export function NetworkNotice({ issue }: { issue: NetworkIssue | null }) {
  // While call controls are still down, the existing "controls are reconnecting" notice says it.
  if (!issue || issue.kind === 'controls_down') return null;
  const open = issue.endedAt === null;
  return (
    <p className={`notice net-notice${open ? ' notice-warn' : ''}`} role="status">
      <Icon icon={WifiOff} /> <span>{networkIssueText(issue, formatClock)} <span className="meta">{NETWORK_ISSUE_SOURCE}</span></span>
    </p>
  );
}

function PlaybackBlocked() {
  const ws = useWorkspace();
  if (ws.bridge.media.playback !== 'blocked') return null;
  return (
    <p className="notice notice-warn">
      The browser paused call audio.{' '}
      <button type="button" className="btn btn-small" onClick={() => void ws.phone.resumeAudio()}><Icon icon={Volume2} /> Turn on call audio</button>
    </p>
  );
}

/**
 * The dock's call frame (Allen, October 30-31, 2026: "it moves up down left right and it's nauseating"). One fixed
 * geometry shared by the ringing, starting and live cards: head, name, one meta line, one notice slot, the four control
 * slots and End, then a tray that scrolls inside itself. Rows have fixed heights, a control that is not available keeps
 * its cell, and the keypad, transfer form and transfer banner swap inside the tray, so nothing above it ever moves.
 * The transcript, coach and CRM card live in the call window, not here.
 */
function EmptySlot() {
  return <span className="control-slot-empty" aria-hidden="true" />;
}

export function IncomingCard({ call, guard }: { call: Call; guard: ActivationGuard }) {
  const ws = useWorkspace();
  const party = partyName(call, ws.userId, ws.userName);
  const lineWords = callLineWords(call, ws.numbers.value);
  const engineCall = ws.callControl.engineCall(call);
  const pending = ws.callControl.pending(call);
  const answer = controlGate(call, 'answer', { pending, readiness: ws.readiness, engineCall });
  const reject = controlGate(call, 'reject', { pending, readiness: ws.readiness, engineCall });
  const now = Date.now();
  guard.observe(`answer:${call.id}`, String(answer.enabled), now);
  guard.observe(`reject:${call.id}`, String(reject.enabled), now);
  const [pressed, setPressed] = useState<ActionName | null>(null);
  const run = (action: ActionName) => {
    if (action !== 'reject' && action !== 'hangup' && !guard.allow(`${action}:${call.id}`, Date.now())) return;
    // Inside the click: the call window opens now or never (browsers block pop-ups outside a gesture).
    if (action === 'answer') armCallWindowAnswer(ws, call.id);
    setPressed(action);
    void ws.callControl.act(call, action);
  };
  const busy = (action: ActionName) => (pending && pressed === action) || undefined;
  // The same timer the answered card shows, already counting while it rings, so answering never resets it.
  const ticking = useNow(TICKING_STATES.has(call.state));
  return (
    <section className="incoming-card call-frame" aria-label={`Incoming call from ${party}`}>
      <div className="call-head" data-slot="head">
        <p className="card-kicker"><span className="dot dot-ring" aria-hidden="true" /><Icon icon={PhoneIncoming} /> <span>Ringing</span> <span className="visually-hidden">Incoming call</span></p>
        <span className="num ring-timer" role="timer" aria-live="off" aria-label="Call time">{formatDuration(elapsedMs(call, ticking))}</span>
      </div>
      <p className="party-name" data-slot="name" title={party}>{party}</p>
      <div className="call-frame-meta" data-slot="meta">
        {call.direction === 'inbound' && call.from.e164 ? (
          <p className="party-meta">{formatPhone(call.from.e164)}{lineWords ? ` (${lineWords})` : ''}</p>
        ) : (
          <p className="party-meta">{lineWords ? `${lineWords} — ` : ''}Internal call</p>
        )}
      </div>
      <div className="dock-notice-slot" data-slot="notice">
        <StatusLine call={call} />
        {/* While Answer is in flight its own button says so; the "waiting to confirm" reason would only flash here. */}
        {!answer.enabled && answer.reason && !pending ? <p className="inline-reason" id={`ans-why-${call.id}`}>{answer.reason}</p> : null}
      </div>
      <div className="call-controls-row" data-slot="controls" role="group" aria-label="Answer or decline">
        <div className="pair-actions frame-answer-row">
          {reject.visible ? (
            <button type="button" className="btn btn-end btn-call" onClick={() => run('reject')} disabled={!reject.enabled} aria-busy={busy('reject')} aria-keyshortcuts="Alt+Shift+E">
              <Icon icon={PhoneOff} /> Decline
            </button>
          ) : <EmptySlot />}
          {answer.visible ? (
            <button type="button" className="btn btn-live btn-call" onClick={() => run('answer')} disabled={!answer.enabled} aria-busy={busy('answer')} aria-describedby={!answer.enabled && !pending ? `ans-why-${call.id}` : undefined} aria-keyshortcuts="Alt+Shift+A">
              <Icon icon={Phone} /> Answer
            </button>
          ) : <EmptySlot />}
        </div>
        {/* Left empty on purpose: End appears here once the call is answered, never under the Answer button's press. */}
        <div className="call-end-cell" aria-hidden="true" />
      </div>
      <div className="call-tray" data-slot="tray">
        <div className="call-tray-view" data-view="call">
          <ContextCard call={call} />
        </div>
      </div>
    </section>
  );
}

/** An outbound call the engine has sent but the server has not yet confirmed (no call ID). */
export function StartingCard({ engineCall }: { engineCall: BridgeCall }) {
  const ws = useWorkspace();
  const unconfirmed = engineCall.phase === 'unconfirmed';
  const status = unconfirmed ? 'Still waiting for the phone system to confirm.' : 'Starting call…';
  return (
    <section className="call-card call-frame state-created" aria-label="Starting call">
      <div className="call-head" data-slot="head">
        <p className="card-kicker" title={status}><span className={`dot dot-${unconfirmed ? 'warn' : 'neutral'}`} aria-hidden="true" /><Icon icon={PhoneOutgoing} /> <span>{status}</span></p>
        <p className="call-timer" aria-hidden="true">0:00</p>
      </div>
      <p className="party-name" data-slot="name"><span className="frame-ph" aria-hidden="true" /></p>
      <div className="call-frame-meta" data-slot="meta"><p className="party-meta">Calling</p></div>
      <div className="dock-notice-slot" data-slot="notice" />
      <div className="call-controls-row" data-slot="controls" role="group" aria-label="Call controls">
        <div className="controls-grid controls-fixed" style={{ ['--cols' as string]: '4' }}>
          <EmptySlot /><EmptySlot /><EmptySlot /><EmptySlot />
        </div>
        <div className="call-end-cell">
          <button type="button" className="btn btn-end btn-endcall" onClick={() => void ws.callControl.cancelStarting(engineCall.key)}>
            <Icon icon={PhoneOff} /> Cancel call
          </button>
        </div>
      </div>
      <div className="call-tray" data-slot="tray">
        <div className="call-tray-view" data-view="call">
          <CallWindowDockRow callId={null} />
        </div>
      </div>
    </section>
  );
}

type TrayView = 'call' | 'keypad' | 'transfer';
/** Window event the Alt+Shift+N shortcut sends before it focuses the note field, which only the call view shows. */
export const DOCK_TRAY_EVENT = 'sidetone-dock-tray';

export function ActiveCallCard({ call, guard }: { call: Call; guard: ActivationGuard }) {
  const ws = useWorkspace();
  const party = partyName(call, ws.userId, ws.userName);
  const who = callWho(call, ws.userId, ws.userName);
  const displayName = who.name || party;
  const now = useNow(TICKING_STATES.has(call.state));
  const engineCall = ws.callControl.engineCall(call);
  const pending = ws.callControl.pending(call);
  // A card that mounts with a transfer already in progress (a reload mid-transfer) starts on its banner.
  const [view, setView] = useState<TrayView>(() => (call.transfer ? 'transfer' : 'call'));
  const [sent, setSent] = useState('');
  const [keypadError, setKeypadError] = useState<string | null>(null);
  const ctx = { pending, readiness: ws.readiness, engineCall };
  const hold = controlGate(call, 'hold', ctx);
  const resume = controlGate(call, 'resume', ctx);
  const dtmf = controlGate(call, 'dtmf', ctx);
  const hangup = controlGate(call, 'hangup', ctx);
  const canMute = !!engineCall && engineCall.microphone !== 'none';
  const t = Date.now();
  guard.observe(`holdtoggle:${call.id}`, `${hold.enabled}:${resume.enabled}`, t);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const network = useRef<NetworkIssueTracker | null>(null);
  network.current ??= new NetworkIssueTracker();
  const networkIssue = network.current.observe(engineCall, t);
  const transferable = call.state === 'active' || call.state === 'held';

  // A transfer in progress takes the tray (its banner holds Complete, Merge and Cancel); when it clears, the tray
  // goes back to the call.
  const transferring = !!call.transfer;
  const hadTransfer = useRef(transferring);
  useEffect(() => {
    if (transferring) setView('transfer');
    else if (hadTransfer.current) setView(v => (v === 'transfer' ? 'call' : v));
    hadTransfer.current = transferring;
  }, [transferring]);
  useEffect(() => {
    if (!dtmf.visible) setView(v => (v === 'keypad' ? 'call' : v));
    if (!transferable && !transferring) setView(v => (v === 'transfer' ? 'call' : v));
  }, [dtmf.visible, transferable, transferring]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onTray = (e: Event) => {
      const detail = (e as CustomEvent<{ callId?: unknown; view?: unknown }>).detail;
      if (detail?.callId === call.id && (detail.view === 'call' || detail.view === 'keypad' || detail.view === 'transfer')) setView(detail.view);
    };
    window.addEventListener(DOCK_TRAY_EVENT, onTray);
    return () => window.removeEventListener(DOCK_TRAY_EVENT, onTray);
  }, [call.id]);

  // The control a command in flight came from: it carries aria-busy, since the notice slot no longer says "Holding…".
  const [pressed, setPressed] = useState<ActionName | null>(null);
  const busy = (...actions: ActionName[]) => (pending && pressed !== null && actions.includes(pressed)) || undefined;
  const run = (control: string, action: ActionName, digits?: string) => {
    if (action !== 'hangup' && action !== 'reject' && !guard.allow(control, Date.now())) return;
    setPressed(action);
    void ws.callControl.act(call, action, digits);
  };
  const sendDigit = (key: string) => {
    if (!validDtmf(key)) return;
    setKeypadError(null);
    if (!dtmf.enabled) { setKeypadError(dtmf.reason ?? 'Keys can’t be sent right now.'); return; }
    setPressed('dtmf');
    void ws.callControl.act(call, 'dtmf', key).then(() => setSent(prev => (prev + key).slice(-24)));
  };
  const toggle = (next: TrayView) => setView(v => (v === next ? 'call' : next));

  const showHold = hold.visible || resume.visible;
  const holdIsResume = call.state === 'held' || resume.enabled;
  const holdGate = holdIsResume ? resume : hold;
  const humanStatus = humanStatusWords(call, ws.userId, displayName);
  const canonicalState = stateLabel(call, ws.userId, displayName);
  const lineWords = callLineWords(call, ws.numbers.value);
  const recCap = capability(ws.config.value, 'recording.capture');
  const capturePolicyActive = recCap?.status === 'enabled' || recCap?.status === 'degraded';
  const coachingNotice = agentCoachingNotice(call);
  const metaText = call.destination.kind === 'pstn'
    ? `${lineWords ? `${lineWords} — ` : ''}From ${call.from.e164 ? formatPhone(call.from.e164) : 'an assigned number'}`
    : `${lineWords ? `${lineWords} — ` : ''}Internal call. No phone number used.`;

  return (
    <section className={`call-card call-frame state-${call.state}`} aria-labelledby={`call-title-${call.id}`}>
      <div className="call-head" data-slot="head">
        <p className="call-state" title={humanStatus}>
          <span className={`dot dot-${callLamp(call.state)}`} aria-hidden="true" />
          <span>{humanStatus}</span>
          {humanStatus !== canonicalState ? <span className="visually-hidden"> ({canonicalState})</span> : null}
        </p>
        <p className="call-timer" role="timer" aria-live="off" aria-label="Call time">{formatDuration(elapsedMs(call, now))}</p>
      </div>
      <h2 ref={titleRef} id={`call-title-${call.id}`} className="party-name" data-slot="name" title={displayName}>{displayName}</h2>
      <div className="call-frame-meta" data-slot="meta">
        {/* A listening or whispering coach is always disclosed: it takes the meta line, never the shared notice slot. */}
        {coachingNotice ? <CoachingBanner call={call} /> : <p className="party-meta" title={metaText}>{metaText}</p>}
      </div>
      <div className="dock-notice-slot" data-slot="notice">
        {/* Highest priority first; the slot shows only its first notice and keeps its height when there is none. */}
        <PlaybackBlocked />
        <StatusLine call={call} />
        {engineCall?.signaling === 'degraded' ? <p className="notice notice-warn">Call audio is connected. Call controls are reconnecting.</p> : null}
        <MicrophoneWarning compact />
        {!engineCall ? <p className="inline-reason">This browser isn’t connected to this call’s audio, so mute isn’t available here.</p> : null}
      </div>
      <div className="call-controls-row" data-slot="controls" role="group" aria-label="Call controls">
        <div className="controls-grid controls-fixed" style={{ ['--cols' as string]: '4' }}>
          {canMute && engineCall ? (
            <button type="button" className="btn btn-control" aria-pressed={engineCall.muted} onClick={() => ws.callControl.setMuted(call, !engineCall.muted)} aria-keyshortcuts="Alt+Shift+M">
              <Icon icon={engineCall.muted ? MicOff : Mic} /> {engineCall.muted ? 'Unmute' : 'Mute'}
            </button>
          ) : <EmptySlot />}
          {showHold ? (
            <button type="button" className="btn btn-control" onClick={() => run(`holdtoggle:${call.id}`, holdIsResume ? 'resume' : 'hold')} disabled={!holdGate.enabled} aria-busy={busy('hold', 'resume')} title={holdGate.reason ?? undefined} aria-keyshortcuts="Alt+Shift+H">
              <Icon icon={holdIsResume ? Play : Pause} /> {holdIsResume ? 'Resume' : 'Hold'}
            </button>
          ) : <EmptySlot />}
          {dtmf.visible ? (
            <button type="button" className="btn btn-control" aria-expanded={view === 'keypad'} aria-controls={`keypad-${call.id}`} aria-busy={busy('dtmf')} onClick={() => toggle('keypad')}>
              <Icon icon={Grid3x3} /> Keypad
            </button>
          ) : <EmptySlot />}
          {transferable ? (
            <button type="button" className="btn btn-control" aria-expanded={view === 'transfer'} aria-controls={`transfer-${call.id}`} onClick={() => toggle('transfer')}>
              <Icon icon={PhoneForwarded} /> Transfer
            </button>
          ) : <EmptySlot />}
        </div>
        <div className="call-end-cell">
          {hangup.visible ? (
            <button type="button" className="btn btn-end btn-endcall" onClick={() => run(`hangup:${call.id}`, 'hangup')} disabled={!hangup.enabled} aria-busy={busy('hangup')} title={hangup.reason ?? undefined} aria-keyshortcuts="Alt+Shift+E">
              <Icon icon={PhoneOff} /> {call.state === 'created' || call.state === 'ringing' ? 'Cancel call' : 'End call'}
            </button>
          ) : <p className="inline-reason">No call controls are available in this state.</p>}
        </div>
      </div>
      <div className="call-tray" data-slot="tray" data-view={view}>
        <div className="call-tray-view" data-view="call" hidden={view !== 'call'}>
          {/* First in the tray, where the starting card has it: nothing above it can grow and push it out of view at
              the moment a blocked pop-up needs it. */}
          <CallWindowDockRow callId={call.id} />
          {/* Recording sits in a slot of its own fixed height, mounted from the first paint: a quiet placeholder until
              the call connects and its state is known, then the band, which scrolls inside the slot as it changes. */}
          {capturePolicyActive ? (
            <div className="capture-slot" data-slot="capture">
              <CaptureBand call={call} headingLevel={3} quiet />
            </div>
          ) : null}
          <CallNoteField call={call} />
          <details className="diagnostics call-tech-details">
            <summary>Technical details</summary>
            <CallStrip call={call} party={party} engineCall={engineCall} />
            {!capturePolicyActive ? <CaptureBand call={call} headingLevel={3} /> : null}
            <NetworkNotice issue={networkIssue} />
          </details>
          {/* Round 13: the other way to finish a call you placed: leave a message and move on. Last in the tray, because
              its reason line changes with the call and nothing below it may move. */}
          <DropVoicemail call={call} guard={guard} />
        </div>
        <div className="call-tray-view" data-view="keypad" hidden={view !== 'keypad'}>
          {dtmf.visible ? (
            <div id={`keypad-${call.id}`} className="keypad" role="group" aria-label="Send keys to the call">
              <div className="dialpad">
                {DIAL_KEYS.map(k => (
                  <button key={k} type="button" className="dial-key" onClick={() => sendDigit(k)} aria-label={`Send ${k === '*' ? 'star' : k === '#' ? 'pound' : k}`}>
                    <span className="dial-digit">{k}</span>
                  </button>
                ))}
              </div>
              <p className="keypad-sent" aria-live="polite">{sent ? `Sent ${sent.split('').join(' ')}` : 'No keys sent yet.'}</p>
              <p className="field-error keypad-error" role="alert">{keypadError ?? ''}</p>
            </div>
          ) : null}
        </div>
        <div className="call-tray-view" data-view="transfer" id={`transfer-${call.id}`} hidden={view !== 'transfer'}>
          <TransferControl call={call} embedded onDone={() => setView('call')} />
        </div>
      </div>
    </section>
  );
}

/** Text typed in the card's note field and not saved yet (saving empties the field). */
const unsavedNoteIn = (card: HTMLElement | null) => !!card?.querySelector('textarea')?.value.trim();

/**
 * After-call wrap-up (UX spec 6.2.10, SALE-08): the call that just ended, its outcome summary, its note
 * field (the in-call draft carries over) and Done. The countdown pauses while the person is using the
 * card or has an unsaved note in it, and "Keep open" stops it (WCAG 2.2.1). Done is an outline button:
 * on the Line a primary button is brass, which stays for Call and Answer. Opening the call's page hands
 * the note over to that page and ends the wrap-up, so one draft is never offered in two places.
 * Rules: wrapUp.ts.
 */
export function WrapUpCard({ call, wrapUp, dispatch, onDone, onOpenPage, focusOnMount = false }: { call: Call; wrapUp: NonNullable<WrapUp>; dispatch: (event: WrapUpEvent) => void; onDone: (noteUnsaved: boolean) => void; onOpenPage: () => void; focusOnMount?: boolean }) {
  const ws = useWorkspace();
  const party = partyName(call, ws.userId, ws.userName);
  const line = call.from.number_id ? ws.numbers.value?.find(n => n.id === call.from.number_id) ?? null : null;
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const [focusIn, setFocusIn] = useState(false);
  const [hover, setHover] = useState(false);
  const [noteUnsaved, setNoteUnsaved] = useState(false);
  const using = useRef(false);
  using.current = focusIn || hover;

  useEffect(() => {
    let last = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const note = unsavedNoteIn(sectionRef.current);
      setNoteUnsaved(note);
      dispatch({ type: 'tick', elapsedMs: now - last, paused: using.current || note });
      last = now;
    }, WRAP_UP_TICK_MS);
    return () => clearInterval(timer);
  }, [dispatch]);
  // The live card this replaces held focus: land on this card's title rather than the page body.
  useEffect(() => { if (focusOnMount) titleRef.current?.focus(); }, [focusOnMount]);
  // "Keep open" goes away once pressed; Done is the next control.
  useEffect(() => { if (wrapUp.keptOpen) doneRef.current?.focus(); }, [wrapUp.keptOpen]);

  const pause: WrapUpPause = noteUnsaved ? 'note' : focusIn || hover ? 'using' : null;
  const failed = call.state === 'failed';
  // Ended here with End: the server's ended event may still be on its way, so the call can read active for a moment.
  const confirmed = callHasEnded(call);
  const openPage = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // The page reads the tab's draft as it opens; give it the latest text, not the last 2-second autosave.
    const text = sectionRef.current?.querySelector('textarea')?.value ?? '';
    if (text.trim()) sessionDrafts().set(draftKey(ws.session.tenant?.id, ws.userId, call.id), text);
    onOpenPage();
  };
  return (
    <section
      ref={sectionRef}
      className={`call-card state-${confirmed ? call.state : 'ended'}`}
      aria-labelledby={`wrap-title-${call.id}`}
      onFocus={() => setFocusIn(true)}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusIn(false); }}
      onPointerEnter={e => { if (e.pointerType === 'mouse') setHover(true); }}
      onPointerLeave={() => setHover(false)}
      onInput={() => setNoteUnsaved(unsavedNoteIn(sectionRef.current))}
    >
      <div className="call-head">
        <p className="call-state"><span className={`dot dot-${failed ? 'end' : 'neutral'}`} aria-hidden="true" />{failed ? 'Call failed' : 'Call ended'}</p>
        <p className="call-timer"><span className="visually-hidden">Talk time </span>{formatDuration(call.talk_ms)}</p>
      </div>
      <h2 ref={titleRef} id={`wrap-title-${call.id}`} className="party-name" tabIndex={-1}><span className="visually-hidden">Wrap-up: </span>{party}</h2>
      <p className="party-meta">{confirmed ? wrapUpSummary(call) : 'Ended.'}</p>
      {line ? <p className="party-meta">{call.direction === 'inbound' ? 'On' : 'From'} <NumberTag number={line} /></p> : call.direction === 'internal' ? <p className="party-meta">Internal call. No phone number used.</p> : null}
      <CallNoteField call={call} />
      <p className="meta">{wrapUpCountdown(wrapUp, pause)}</p>
      <div className="pair-actions">
        {!wrapUp.keptOpen ? <button type="button" className="btn btn-quiet" onClick={() => dispatch({ type: 'keep_open' })}>Keep open</button> : null}
        <button ref={doneRef} type="button" className="btn" onClick={() => onDone(unsavedNoteIn(sectionRef.current))}>Done</button>
      </div>
      <p className="field-hint"><Link to={`/inbox/calls/${encodeURIComponent(call.id)}`} onClick={openPage}>Open the call’s page</Link></p>
      <PostCallFollowupSection call={call} compact />
      <ContextCard call={call} />
    </section>
  );
}
